import cookie from 'js-cookie'
import _ from 'lodash'
import {createContext, FC, useCallback, useContext, useEffect, useState} from 'react'

import {getCurrentSearchParams} from '@/utils/browser/location'
import {setAnalyticsDebugConfig} from '@/utils/tracking/analytics'

const DEBUG_COOKIE = 'debugEnabled'

export enum FeatureFlags {
  // Log every analytics event to the console (dev visibility).
  logAnalyticsToConsole = 'logAnalyticsToConsole',
  // Whether analytics events are actually sent to Mixpanel/GA/GTM.
  sendAnalyticsEvents = 'sendAnalyticsEvents',
}

// Default flag values used when no cookie override is present.
const FeatureFlagsEnv: Record<FeatureFlags, boolean> = {
  [FeatureFlags.logAnalyticsToConsole]: false,
  [FeatureFlags.sendAnalyticsEvents]: true,
}

type DebugContextType = {
  debugEnabled: boolean
  setFlagValue: (key: FeatureFlags, value: boolean) => void
  getFlagValue: (key: FeatureFlags) => boolean
  isDebugModalOpen: boolean
  closeDebugModal: () => void
}

const DebugContext = createContext<DebugContextType>({
  debugEnabled: false,
  setFlagValue: (key: FeatureFlags, value: boolean) => {
    cookie.set(key, String(value))
  },
  getFlagValue: (key: FeatureFlags): boolean => {
    return Boolean(cookie.get(key))
  },
  isDebugModalOpen: false,
  closeDebugModal: _.identity,
})

export const useDebug = (): DebugContextType => useContext(DebugContext)

// when setting initial values, cookies override .env
const getInitFlagValue = (key: FeatureFlags): boolean => {
  const cookieValue = cookie.get(key)

  if (cookieValue) {
    return cookie.get(key) === 'true'
  }

  return FeatureFlagsEnv[key]
}

export const DebugProvider: FC<React.PropsWithChildren<unknown>> = ({children}) => {
  const [featureFlags, setFeatureFlag] = useState<Record<FeatureFlags, boolean>>(() =>
    // @ts-expect-error I guarantee correctness
    _.mapValues(FeatureFlagsEnv, (value, key) => getInitFlagValue(key)),
  )

  const [debugEnabled, setDebugEnabled] = useState(false)
  const [isDebugModalOpen, setIsDebugModalOpen] = useState(false)

  // `?debug=true|false` and the cookie are both read here rather than during render: this
  // provider wraps every page, so reading the query with `useSearchParams()` would opt the whole
  // app out of static rendering
  useEffect(() => {
    const debugParam = getCurrentSearchParams().get('debug')

    if (debugParam === 'false') {
      cookie.remove(DEBUG_COOKIE)
      return
    }
    if (debugParam === 'true') {
      cookie.set(DEBUG_COOKIE, 'true')
      setDebugEnabled(true)
      return
    }
    if (cookie.get(DEBUG_COOKIE) === 'true') {
      setDebugEnabled(true)
    }
  }, [])

  const setFlagValue = useCallback((key: FeatureFlags, value: boolean) => {
    cookie.set(key, String(value))
    setFeatureFlag((state) => ({...state, [key]: value}))
  }, [])

  const getFlagValue = useCallback(
    (key: FeatureFlags): boolean => {
      return Boolean(featureFlags[key])
    },
    [featureFlags],
  )

  // Bridge the analytics debug flags into the (non-React) analytics module so
  // track/identify/page calls can honor them.
  useEffect(() => {
    setAnalyticsDebugConfig({
      logToConsole: getFlagValue(FeatureFlags.logAnalyticsToConsole),
      sendToProviders: getFlagValue(FeatureFlags.sendAnalyticsEvents),
    })
  }, [featureFlags, getFlagValue])

  const shortcutListener = (e: KeyboardEvent) => {
    if ((e.key === 'd' || e.code === 'KeyD') && e.altKey && e.ctrlKey) {
      setIsDebugModalOpen(true)
    }
  }

  useEffect(() => {
    if (document && debugEnabled) {
      document.addEventListener('keydown', shortcutListener)
    }

    if (debugEnabled) {
      return () => {
        if (document) {
          document.removeEventListener('keydown', shortcutListener)
        }
      }
    }
  }, [debugEnabled])

  return (
    <DebugContext.Provider
      value={{
        debugEnabled,
        getFlagValue,
        setFlagValue,
        isDebugModalOpen,
        closeDebugModal: () => setIsDebugModalOpen(false),
      }}
    >
      {children}
    </DebugContext.Provider>
  )
}
